import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar, Users, Trash2, Pencil, Plus, QrCode, Copy, MessageCircle, Download } from "lucide-react";
import { Link } from "react-router-dom";

const MODULES = ["Découverte", "Progression", "Pilote", "Coaching individuel", "Team building"];
const STATUSES = ["open", "full", "closed"];
const LEVELS = ["débutant", "amateur", "expert", "pro"];

const emptySession = {
  date: "", time: "18:00", duration_min: 60, module: "Découverte",
  coach: "", capacity: 1, booked: 0, price_eur: 89, circuit: "", notes: "", status: "open",
};

export default function AdminSimulator() {
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySession);
  const [qrSession, setQrSession] = useState(null);

  const publicOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const shareLink = (sid) => `${publicOrigin}/simulator?s=${sid}`;

  const copyLink = async (sid) => {
    try {
      await navigator.clipboard.writeText(shareLink(sid));
      toast.success("Lien copié");
    } catch { toast.error("Copie impossible"); }
  };

  const downloadQR = (sid) => {
    const svg = document.getElementById(`qr-svg-${sid}`);
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 800; canvas.height = 800;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 800, 800);
      ctx.drawImage(img, 40, 40, 720, 720);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png; a.download = `kv-session-${sid}.png`;
      a.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const shareWhatsApp = (s) => {
    const dateStr = new Date(s.date + "T" + (s.time || "12:00")).toLocaleDateString("fr-BE", { weekday: "long", day: "numeric", month: "long" });
    const txt = `Session coaching simulateur KV International — ${s.module} · ${dateStr} à ${s.time}${s.circuit ? " · " + s.circuit : ""}${s.coach ? " · Coach " + s.coach : ""}${s.price_eur ? " · " + s.price_eur + " €" : ""}\n\nRéservez en un clic : ${shareLink(s.id)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener");
  };

  const loadSessions = () => api.get("/sim-sessions", { params: { upcoming: false } }).then((r) => setSessions(r.data));
  const loadBookings = () => api.get("/coaching-bookings").then((r) => setBookings(r.data));

  useEffect(() => { loadSessions(); loadBookings(); }, []);

  const openNew = () => { setForm({ ...emptySession, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (s) => { setForm({ ...emptySession, ...s }); setOpen(true); };

  const save = async () => {
    try {
      // Retire les champs non attendus par le modèle Pydantic pour éviter tout rejet
      const { id, created_at, ...rest } = form;
      const payload = {
        ...rest,
        duration_min: Number(rest.duration_min) || 60,
        capacity: Number(rest.capacity) || 1,
        booked: Number(rest.booked) || 0,
        price_eur: Number(rest.price_eur) || 0,
      };
      if (id) await api.put(`/sim-sessions/${id}`, payload);
      else await api.post("/sim-sessions", payload);
      toast.success(id ? "Session mise à jour" : "Session créée");
      setOpen(false); loadSessions();
    } catch (e) {
      console.error("save session error:", e, e?.response?.data);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : formatApiErrorDetail(detail) || e?.message || "Enregistrement impossible";
      toast.error(msg);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette session ?")) return;
    await api.delete(`/sim-sessions/${id}`); toast.success("Supprimée"); loadSessions();
  };

  const setBookingStatus = async (id, status) => {
    await api.patch(`/coaching-bookings/${id}`, { status });
    toast.success("Statut mis à jour"); loadBookings();
  };
  const delBooking = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/coaching-bookings/${id}`); toast.success("Supprimée"); loadBookings();
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-simulator">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-white leading-[1.1]">Coaching Simulateur</h1>
          <p className="text-white/50 text-sm mt-2">Gérez le calendrier des sessions, les réservations et les tarifs de coaching.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/site" className="text-xs uppercase tracking-widest text-white/60 border border-white/15 px-4 py-3 rounded-md hover:text-[#C8A75A] hover:border-[#C8A75A]/40">Modifier les tarifs</Link>
          {tab === "sessions" && (
            <Button data-testid="new-session-btn" onClick={openNew} className="bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle session
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-white/10 mb-8">
        {[
          { id: "sessions", label: `Sessions (${sessions.length})`, icon: Calendar },
          { id: "bookings", label: `Réservations (${bookings.length})`, icon: Users },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-sim-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.25em] border-b-2 -mb-[1px] ${
              tab === t.id ? "text-[#C8A75A] border-[#C8A75A]" : "text-white/50 border-transparent hover:text-white"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "sessions" && (
        <div data-testid="sessions-list" className="bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden">
          {sessions.length === 0 ? (
            <p className="p-8 text-white/40 text-sm">Aucune session programmée. Cliquez sur « Nouvelle session ».</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.25em] text-white/40 border-b border-white/10">
                <tr>
                  <th className="text-left p-4">Date · Heure</th>
                  <th className="text-left p-4">Module</th>
                  <th className="text-left p-4">Coach</th>
                  <th className="text-left p-4">Circuit</th>
                  <th className="text-left p-4">Places</th>
                  <th className="text-left p-4">Prix</th>
                  <th className="text-left p-4">Statut</th>
                  <th className="text-right p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} data-testid={`session-row-${s.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-white">
                      <div className="font-medium">{s.date}</div>
                      <div className="text-white/50 text-xs">{s.time} · {s.duration_min} min</div>
                    </td>
                    <td className="p-4 text-white/80">{s.module}</td>
                    <td className="p-4 text-white/80">{s.coach || "—"}</td>
                    <td className="p-4 text-white/80">{s.circuit || "—"}</td>
                    <td className="p-4">
                      <span className={`text-xs ${s.booked >= s.capacity ? "text-red-400" : "text-[#C8A75A]"}`}>{s.booked}/{s.capacity}</span>
                    </td>
                    <td className="p-4 text-white/80">{s.price_eur > 0 ? `${s.price_eur} €` : "—"}</td>
                    <td className="p-4">
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded ${
                        s.status === "open" ? "bg-emerald-500/15 text-emerald-400" :
                        s.status === "full" ? "bg-amber-500/15 text-amber-400" :
                        "bg-white/10 text-white/50"
                      }`}>{s.status}</span>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => setQrSession(s)} data-testid={`qr-session-${s.id}`} title="Partager (QR + lien)" className="text-[#C8A75A] hover:text-white p-2"><QrCode className="w-4 h-4" /></button>
                      <button onClick={() => openEdit(s)} data-testid={`edit-session-${s.id}`} className="text-white/60 hover:text-[#C8A75A] p-2"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(s.id)} data-testid={`del-session-${s.id}`} className="text-white/60 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "bookings" && (
        <div data-testid="bookings-list" className="bg-[#1E1E1E] border border-white/10 rounded-md overflow-hidden">
          {bookings.length === 0 ? (
            <p className="p-8 text-white/40 text-sm">Aucune demande de coaching pour l'instant.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.25em] text-white/40 border-b border-white/10">
                <tr>
                  <th className="text-left p-4">Reçue le</th>
                  <th className="text-left p-4">Nom</th>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Niveau</th>
                  <th className="text-left p-4">Circuit</th>
                  <th className="text-left p-4">Statut</th>
                  <th className="text-right p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
                    <td className="p-4 text-white/70 text-xs">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-4 text-white font-medium">{b.name}</td>
                    <td className="p-4 text-white/70 text-xs">
                      <div>{b.email}</div>
                      {b.phone && <div>{b.phone}</div>}
                    </td>
                    <td className="p-4 text-white/70">{b.level}</td>
                    <td className="p-4 text-white/70">{b.circuit_pref || "—"}</td>
                    <td className="p-4">
                      <Select value={b.status} onValueChange={(v) => setBookingStatus(b.id, v)}>
                        <SelectTrigger data-testid={`booking-status-${b.id}`} className="h-8 w-[130px] bg-[#0D0D0D] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                          {["new", "confirmed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4 text-right">
                      <a href={`mailto:${b.email}?subject=${encodeURIComponent("KV International — Coaching simulateur")}`} className="text-xs text-[#C8A75A] hover:text-white uppercase tracking-widest">Répondre</a>
                      <button onClick={() => delBooking(b.id)} className="text-white/50 hover:text-red-400 p-2 ml-2"><Trash2 className="w-4 h-4" /></button>
                      {b.message && (
                        <details className="text-left mt-2">
                          <summary className="text-[10px] uppercase text-white/40 tracking-widest cursor-pointer">Message</summary>
                          <p className="text-white/70 text-xs mt-1 whitespace-pre-wrap max-w-md">{b.message}</p>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="session-dialog" className="bg-[#1E1E1E] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">{form.id ? "Modifier la session" : "Nouvelle session"}</DialogTitle>
            <DialogDescription className="text-white/50 text-xs">Renseignez les infos de la session coaching simulateur.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4 mt-2">
            <F label="Date"><Input type="date" data-testid="ses-date" value={form.date} onChange={(e) => setF("date", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Heure"><Input type="time" data-testid="ses-time" value={form.time} onChange={(e) => setF("time", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Durée (min)"><Input type="number" value={form.duration_min} onChange={(e) => setF("duration_min", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Module">
              <Select value={form.module} onValueChange={(v) => setF("module", v)}>
                <SelectTrigger data-testid="ses-module" className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Coach"><Input value={form.coach} onChange={(e) => setF("coach", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="Ex : Yannick Latour" /></F>
            <F label="Circuit"><Input value={form.circuit} onChange={(e) => setF("circuit", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="Ex : Spa-Francorchamps" /></F>
            <F label="Capacité"><Input type="number" value={form.capacity} onChange={(e) => setF("capacity", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Déjà réservés"><Input type="number" value={form.booked} onChange={(e) => setF("booked", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Prix (€)"><Input type="number" value={form.price_eur} onChange={(e) => setF("price_eur", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></F>
            <F label="Statut">
              <Select value={form.status} onValueChange={(v) => setF("status", v)}>
                <SelectTrigger className="bg-[#0D0D0D] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10 text-white">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
          <F label="Notes (public)">
            <Textarea rows={3} value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="Description affichée sur la fiche session côté public" />
          </F>
          <div className="flex justify-end gap-3 mt-4">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-white/60">Annuler</Button>
            <Button type="button" data-testid="save-session" onClick={save} className="bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">{label}</label>
      {children}
    </div>
  );
}
